import { twMerge } from "tailwind-merge";
import { useCallback } from "react";
import { m } from "framer-motion";
import IeltsInputResultIcon from "../ResultIcon";
import useIeltsTheme from "../../hooks/useIeltsTheme";
import { IeltsSkill } from "@/__generated__/graphql";

type TOption = {
  label: React.ReactNode;
  value: string;
  title: React.ReactNode;
  result?: boolean;
  readonly?: boolean;
};

type Props = {
  options: TOption[];
  value: string[];
  className?: string;
  disabled?: boolean;
  onChange?: (value: string[]) => void;
  limit?: number;
  readonly?: boolean;
  id?: string;
  useIndexLabel?: boolean;
};

function Option({
  value,
  label,
  title,
  checked,
  result,
  readonly,
  onClick,
}: {
  checked?: boolean;
  onClick?: () => void;
} & TOption) {
  const { theme } = useIeltsTheme();

  return (
    <div
      id="do-not-select"
      className={twMerge(
        "flex items-center w-fit gap-x-3 cursor-pointer relative",
        readonly ? "cursor-default" : "",
      )}
      onClick={onClick}
      role="button"
      onKeyDown={undefined}
      tabIndex={-1}
      key={value}
    >
      <div
        className={twMerge(
          "shrink-0  border-[1px] w-[26px] h-[26px] flex items-center justify-center rounded-full select-none",
          // The CLASSIC_LIGHT_ORANGE arm read `shadow-[0px_2px_0px_##EEDCBA]` —
          // a doubled `#`, so that theme rendered no lift at all.
          "border-ielts-fg shadow-[0px_2px_0px_var(--ielts-fg-muted)]",
        )}
      >
        <span className="text-[13px] font-bold leading-[26px] text-ielts-fg">
          {label}
        </span>
      </div>
      <m.div
        whileTap={readonly ? {} : { scale: 0.8 }}
        className={twMerge(
          "shrink-0 w-[32px] h-[32px] border-[2px] p-[3px] select-none",
          "border-ielts-line-strong",
        )}
      >
        <div
          className={twMerge("w-full h-full")}
          style={{ backgroundColor: checked ? theme.accent : undefined }}
        />
      </m.div>
      <div>{title}</div>

      <IeltsInputResultIcon
        className="absolute -top-[10px] -right-6"
        result={result}
      />
    </div>
  );
}

function IeltsMultipleChoiceInput({
  className = "",
  options,
  value,
  disabled,
  onChange,
  limit,
  readonly,
  id,
  useIndexLabel,
}: Props) {
  const handleClick = useCallback(
    (v: string) => {
      if (disabled) return;

      if (value.includes(v)) {
        onChange?.(value.filter((item) => item !== v));
        return;
      }

      if (limit && value.length >= limit) return;

      onChange?.([...value, v]);
    },
    [disabled, onChange, value, limit],
  );

  return (
    <div id={id} className={twMerge("flex flex-col gap-y-3 ", className)}>
      {options.map((option, oIndex) => {
        const indexLabel = String.fromCharCode(65 + oIndex);
        return (
          <Option
            {...option}
            checked={value.includes(option.value)}
            key={option.value}
            onClick={() => (readonly ? undefined : handleClick(option.value))}
            readonly={readonly}
            label={useIndexLabel ? indexLabel : option.label}
          />
        );
      })}
    </div>
  );
}

export default IeltsMultipleChoiceInput;
